import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Icon } from "@iconify/react";
import MainLayout from "../../layouts/MainLayout";
import apiClient from '../../services/api/apiClient';
import { AccountDropdown, useToast } from '../../components/ui';

function PostsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast, confirm } = useToast();
  const [view, setView] = useState("list"); // list or calendar

  // Get tab from URL param or default to "queue"
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    ['queue', 'drafts', 'approvals'].includes(tabFromUrl) ? tabFromUrl : "queue"
  ); // queue, drafts, approvals
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [scheduledSlot, setScheduledSlot] = useState(null);

  // Load posts
  useEffect(() => {
    loadPosts();
  }, [selectedAccount]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/posts');
      if (response.data?.posts) {
        // Map snake_case from DB to camelCase for frontend
        const mappedPosts = response.data.posts.map(post => ({
          ...post,
          content: post.content_text,
          scheduledFor: post.scheduled_at,
          publishedAt: post.published_at,
          createdAt: post.created_at,
          platform: post.accounts?.platform || 'threads',
          accountName: post.accounts?.account_name
        }));
        setPosts(mappedPosts);
      }
    } catch (error) {
      console.error("Failed to load posts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get next slot time
  function getNextSlotTime(hour, minute) {
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    if (date < new Date()) {
      date.setDate(date.getDate() + 1);
    }
    return date.toISOString();
  }

  // Filter posts by tab
  const filteredPosts = useMemo(() => {
    switch (activeTab) {
      case 'queue':
        return posts.filter(p => p.status === 'scheduled' || p.status === 'approved');
      case 'drafts':
        return posts.filter(p => p.status === 'draft');
      case 'approvals':
        return posts.filter(p => p.status === 'pending_review');
      default:
        return posts;
    }
  }, [posts, activeTab]);

  // Count by status
  const counts = useMemo(() => ({
    queue: posts.filter(p => p.status === 'scheduled' || p.status === 'approved').length,
    drafts: posts.filter(p => p.status === 'draft').length,
    approvals: posts.filter(p => p.status === 'pending_review').length,
  }), [posts]);

  // Handle delete post
  const handleDeletePost = (postId) => {
    setPosts(posts.filter(p => p.id !== postId));
  };

  // Group posts by day for timeline view
  const groupedPosts = useMemo(() => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    filteredPosts.forEach(post => {
      const postDate = new Date(post.scheduledFor || post.publishedAt || post.createdAt);
      const dateKey = postDate.toDateString();

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: postDate,
          label: getDateLabel(postDate),
          posts: []
        };
      }
      groups[dateKey].posts.push(post);
    });

    // Sort groups by date
    return Object.values(groups).sort((a, b) => a.date - b.date);
  }, [filteredPosts]);

  function getDateLabel(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    if (targetDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (targetDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    }
  }

  // Default time slots
  const defaultSlots = ['09:00', '12:30', '18:00', '20:30'];

  // Header actions for MainLayout
  const headerActions = (
    <>
      <button
        onClick={() => setShowCreateModal(true)}
        className="px-4 py-2 bg-gray-900 text-white border-none rounded-lg font-semibold text-sm cursor-pointer flex items-center gap-2 hover:bg-gray-800 transition-colors"
      >
        <Icon icon="mdi:plus" width={18} />
        New Post
      </button>
    </>
  );

  return (
    <MainLayout
      activePage="posts"
      title="Posts"
      subtitle="排程與管理你的社群貼文"
      headerActions={headerActions}
    >
      <div className="flex-1 overflow-y-auto bg-gray-50">
          {/* Tabs + View Toggle Row */}
          <div className="bg-white border-b border-gray-200 px-6">
            <div className="flex items-center justify-between">
              {/* Left: Tabs */}
              <div className="flex">
                {[
                  { id: 'queue', label: 'Queue', count: counts.queue },
                  { id: 'drafts', label: 'Drafts', count: counts.drafts },
                  { id: 'approvals', label: 'Approvals', count: counts.approvals, isPro: true }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 bg-transparent border-none border-b-2 font-medium text-sm cursor-pointer flex items-center gap-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-gray-900 text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        activeTab === tab.id
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                    {tab.isPro && (
                      <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                        PRO
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Right: Timezone + View Toggle */}
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Icon icon="mdi:earth" width={14} />
                  GMT+8
                </span>
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setView('list')}
                    className={`px-3 py-1.5 border-none rounded-md font-medium text-xs cursor-pointer flex items-center gap-1 transition-all ${
                      view === 'list'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'bg-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon icon="mdi:format-list-bulleted" width={16} />
                    List
                  </button>
                  <button
                    onClick={() => setView('calendar')}
                    className={`px-3 py-1.5 border-none rounded-md font-medium text-xs cursor-pointer flex items-center gap-1 transition-all ${
                      view === 'calendar'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'bg-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon icon="mdi:calendar" width={16} />
                    Calendar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <Icon icon="mdi:loading" width={32} className="text-blue-500 animate-spin mx-auto mb-3" />
                  <p className="text-gray-600 text-sm">Loading posts...</p>
                </div>
              </div>
            ) : view === 'list' ? (
              <TimelineView
                groupedPosts={groupedPosts}
                defaultSlots={defaultSlots}
                navigate={navigate}
                activeTab={activeTab}
                onCreatePost={(date, time) => {
                  setScheduledSlot({ date, time });
                  setShowCreateModal(true);
                }}
                onDeletePost={handleDeletePost}
                toast={toast}
                confirm={confirm}
              />
            ) : (
              <CalendarView
                posts={filteredPosts}
                navigate={navigate}
                onCreatePost={(date) => {
                  setScheduledSlot({ date, time: '09:00' });
                  setShowCreateModal(true);
                }}
              />
            )}
          </div>
      </div>

      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal
          account={selectedAccount}
          scheduledSlot={scheduledSlot}
          onClose={(shouldReload = false) => {
            setShowCreateModal(false);
            setScheduledSlot(null);
            if (shouldReload) {
              loadPosts();
            }
          }}
          onSubmit={(postData) => {
            console.log('Creating post:', postData);
            // TODO: Call API to create post
            setShowCreateModal(false);
            setScheduledSlot(null);
            loadPosts();
          }}
        />
      )}
    </MainLayout>
  );
}

// Timeline View Component (Buffer-style)
function TimelineView({ groupedPosts, defaultSlots, navigate, activeTab, onCreatePost, onDeletePost, toast, confirm }) {
  const today = new Date();
  const daysToShow = 7;

  // Generate days array
  const days = [];
  for (let i = 0; i < daysToShow; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    date.setHours(0, 0, 0, 0);
    days.push(date);
  }

  function getDateLabel(date) {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const tomorrow = new Date(todayDate);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    if (targetDate.getTime() === todayDate.getTime()) {
      return 'Today';
    } else if (targetDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    }
  }

  function getPostsForSlot(date, slotTime) {
    const [hours, minutes] = slotTime.split(':').map(Number);
    const slotDate = new Date(date);
    slotDate.setHours(hours, minutes, 0, 0);

    return groupedPosts
      .flatMap(g => g.posts)
      .filter(post => {
        const postDate = new Date(post.scheduledFor || post.createdAt);
        return postDate.getHours() === hours &&
               postDate.getMinutes() === minutes &&
               postDate.toDateString() === date.toDateString();
      });
  }

  if (activeTab === 'drafts' || activeTab === 'sent') {
    // Simple list for drafts and sent
    const posts = groupedPosts.flatMap(g => g.posts);

    return (
      <div className="max-w-3xl mx-auto">
        {posts.length === 0 ? (
          <EmptyState tab={activeTab} navigate={navigate} />
        ) : (
          <div className="flex flex-col gap-3">
            {posts.map(post => (
              <PostCard key={post.id} post={post} onDelete={onDeletePost} toast={toast} confirm={confirm} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {days.map((day, dayIndex) => {
        const dateLabel = getDateLabel(day);
        const dateStr = day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        return (
          <div key={dayIndex} className="mb-8">
            {/* Day Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="font-semibold text-[15px] text-gray-900">
                {dateLabel}
              </div>
              <div className="text-sm text-gray-500">
                {dateStr}
              </div>
            </div>

            {/* Time Slots */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {defaultSlots.map((slot, slotIndex) => {
                const slotPosts = getPostsForSlot(day, slot);

                return (
                  <div key={slot}>
                    {/* Slot Header */}
                    <div className={`flex items-center justify-between px-4 py-3 bg-gray-50 ${
                      slotIndex < defaultSlots.length - 1 || slotPosts.length > 0 ? 'border-b border-gray-100' : ''
                    }`}>
                      <div className="flex items-center gap-2">
                        <Icon icon="mdi:clock-outline" width={16} className="text-gray-400" />
                        <span className="text-sm text-gray-500 font-medium">
                          {slot}
                        </span>
                      </div>

                      <button
                        onClick={() => onCreatePost(day, slot)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-transparent border border-dashed border-gray-300 rounded-md text-gray-500 text-[13px] cursor-pointer font-medium"
                      >
                        <Icon icon="mdi:plus" width={16} />
                        New
                      </button>
                    </div>

                    {/* Posts in this slot */}
                    {slotPosts.map(post => (
                      <div key={post.id} className="border-b border-gray-100">
                        <PostCard post={post} onDelete={onDeletePost} toast={toast} confirm={confirm} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Post Card Component
function PostCard({ post, onDelete, toast, confirm }) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const platformIcons = {
    threads: 'simple-icons:threads',
    instagram: 'mdi:instagram',
    facebook: 'mdi:facebook',
    twitter: 'mdi:twitter'
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: '刪除貼文',
      message: '確定要刪除這則貼文嗎？此操作無法復原。',
      confirmText: '刪除',
      cancelText: '取消',
      variant: 'destructive'
    });

    if (!confirmed) return;

    // Check if this is demo data (simple numeric ID) vs real data (UUID)
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(post.id);

    if (!isUUID) {
      // Demo data - just remove from UI
      onDelete?.(post.id);
      toast.success('貼文已刪除');
      setShowMenu(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/posts/${post.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        onDelete?.(post.id);
        toast.success('貼文已刪除');
      } else {
        toast.error('刪除失敗: ' + (result.message || '未知錯誤'));
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('刪除失敗');
    }
    setShowMenu(false);
  };

  return (
    <div
      className="flex gap-4 p-4 bg-white cursor-pointer transition-colors hover:bg-gray-50"
    >
      {/* Platform Icon */}
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon
          icon={platformIcons[post.platform] || 'mdi:send'}
          width={20}
          className="text-gray-700"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-900 leading-relaxed line-clamp-2">
          {post.content}
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
          {post.scheduledFor && (
            <span className="flex items-center gap-1">
              <Icon icon="mdi:clock-outline" width={14} />
              {new Date(post.scheduledFor).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}
            </span>
          )}
          {post.status === 'draft' && (
            <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-[11px] font-medium">
              Draft
            </span>
          )}
          {(post.status === 'sent' || post.status === 'published') && (
            <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-[11px] font-medium">
              Published
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <button
          className="bg-transparent border-none p-2 rounded-md cursor-pointer text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="編輯"
        >
          <Icon icon="mdi:pencil" width={18} />
        </button>

        {/* More Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="bg-transparent border-none p-2 rounded-md cursor-pointer text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Icon icon="mdi:dots-vertical" width={18} />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[120px] z-50">
              <button
                onClick={handleDelete}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-none bg-transparent cursor-pointer"
              >
                <Icon icon="mdi:trash-can-outline" width={16} />
                刪除
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ tab, navigate }) {
  const messages = {
    queue: {
      icon: 'mdi:calendar-clock',
      title: 'No posts scheduled',
      description: 'Schedule your first post to get started'
    },
    drafts: {
      icon: 'mdi:file-document-outline',
      title: 'No drafts yet',
      description: 'Save posts as drafts to edit later'
    },
    approvals: {
      icon: 'mdi:check-circle-outline',
      title: 'No posts awaiting approval',
      description: 'Posts requiring approval will appear here'
    }
  };

  const msg = messages[tab] || messages.queue;

  return (
    <div className="text-center py-16 px-5 bg-white rounded-xl border border-gray-200">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <Icon icon={msg.icon} width={28} className="text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-2">
        {msg.title}
      </h3>
      <p className="text-sm text-gray-500 mb-5">
        {msg.description}
      </p>
      <button
        onClick={() => navigate('/create-content')}
        className="px-5 py-2.5 bg-gray-900 text-white border-none rounded-lg font-semibold text-sm cursor-pointer"
      >
        Create Post
      </button>
    </div>
  );
}

// Calendar View Component
function CalendarView({ posts, navigate, onCreatePost }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDay = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const weeks = [];
  let days = [];

  // Add empty cells for days before the first day of month
  for (let i = 0; i < startDay; i++) {
    days.push(null);
  }

  // Add days of month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
    if (days.length === 7) {
      weeks.push(days);
      days = [];
    }
  }

  // Add remaining days
  if (days.length > 0) {
    while (days.length < 7) {
      days.push(null);
    }
    weeks.push(days);
  }

  const getPostsForDay = (day) => {
    if (!day) return [];
    const date = new Date(year, month, day);
    return posts.filter(post => {
      const postDate = new Date(post.scheduledFor || post.publishedAt || post.createdAt);
      return postDate.toDateString() === date.toDateString();
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Calendar Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <button
          onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
          className="bg-transparent border-none p-2 cursor-pointer rounded-md"
        >
          <Icon icon="mdi:chevron-left" width={24} className="text-gray-500" />
        </button>

        <h3 className="text-base font-semibold text-gray-900">
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>

        <button
          onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
          className="bg-transparent border-none p-2 cursor-pointer rounded-md"
        >
          <Icon icon="mdi:chevron-right" width={24} className="text-gray-500" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-3 text-center text-xs font-semibold text-gray-500 uppercase">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      {weeks.map((week, weekIndex) => (
        <div key={weekIndex} className={`grid grid-cols-7 ${
          weekIndex < weeks.length - 1 ? 'border-b border-gray-100' : ''
        }`}>
          {week.map((day, dayIndex) => {
            const dayPosts = getPostsForDay(day);
            const isToday = day && new Date().toDateString() === new Date(year, month, day).toDateString();

            return (
              <div
                key={dayIndex}
                onClick={() => day && onCreatePost(new Date(year, month, day))}
                className={`min-h-[100px] p-2 ${
                  dayIndex < 6 ? 'border-r border-gray-100' : ''
                } ${
                  isToday ? 'bg-blue-50' : day ? 'bg-white' : 'bg-gray-50'
                } ${
                  day ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                {day && (
                  <>
                    <div className={`text-[13px] mb-1 ${
                      isToday ? 'font-semibold text-blue-600' : 'font-normal text-gray-700'
                    }`}>
                      {day}
                    </div>
                    {dayPosts.slice(0, 2).map(post => (
                      <div
                        key={post.id}
                        className="text-[11px] px-1.5 py-1 bg-indigo-100 text-indigo-800 rounded mb-0.5 overflow-hidden text-ellipsis whitespace-nowrap"
                      >
                        {post.content?.substring(0, 20)}...
                      </div>
                    ))}
                    {dayPosts.length > 2 && (
                      <div className="text-[10px] text-gray-500">
                        +{dayPosts.length - 2} more
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Create Post Modal Component (Buffer-style)
function CreatePostModal({ account: initialAccount, scheduledSlot, onClose, onSubmit }) {
  const { toast } = useToast();
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [postType, setPostType] = useState('post'); // post, reel, story
  const [scheduleDate, setScheduleDate] = useState(scheduledSlot?.date || new Date());
  const [scheduleTime, setScheduleTime] = useState(scheduledSlot?.time || '09:00');
  const [createAnother, setCreateAnother] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(initialAccount?.id || null);
  const [selectedAccount, setSelectedAccount] = useState(initialAccount || null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // AI Assistant states
  const [showAIOptions, setShowAIOptions] = useState(false);
  const [aiLength, setAiLength] = useState('short'); // short, long
  const [aiTone, setAiTone] = useState('casual'); // casual, professional
  const [isAIGenerating, setIsAIGenerating] = useState(false);

  // Publishing states
  const [isPublishing, setIsPublishing] = useState(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);

  // Handle account change from dropdown
  const handleAccountChange = (accountId, account) => {
    setSelectedAccountId(accountId);
    setSelectedAccount(account);
  };

  // AI Assistant: Format text with line breaks (social media style)
  const handleAIFormat = () => {
    if (!content.trim()) return;

    // 先按原有段落分割（保留空行結構）
    const paragraphs = content.split(/\n\s*\n/);

    const formatParagraph = (text) => {
      // 標題判斷：短句 + 冒號結尾（如「生存遊戲：」）
      const titleMatch = text.match(/^([^，。！？]{1,15}[：:])(.*)$/s);
      if (titleMatch) {
        const [, title, rest] = titleMatch;
        if (rest.trim()) {
          return title.trim() + '\n' + formatSentences(rest.trim());
        }
        return title.trim();
      }
      return formatSentences(text);
    };

    const formatSentences = (text) => {
      // 正規化空白
      text = text.replace(/\s+/g, ' ').trim();

      let result = '';
      let currentLine = '';
      let inQuote = false; // 追蹤是否在引號內

      // 引號開始符號
      const quoteStart = ['「', '"', '『', '《', '【'];
      // 引號結束符號
      const quoteEnd = ['」', '"', '』', '》', '】'];
      // 句尾標點
      const sentenceEndMarks = ['。', '！', '？', '.', '!', '?'];

      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        currentLine += char;

        // 追蹤引號狀態
        if (quoteStart.includes(char)) {
          inQuote = true;
        } else if (quoteEnd.includes(char)) {
          inQuote = false;
        }

        // 如果在引號內，不換行（保持引用完整）
        if (inQuote) {
          continue;
        }

        // 檢查下一個字元
        const nextChar = text[i + 1];

        // 句尾標點
        const isSentenceEnd = sentenceEndMarks.includes(char);
        // 逗號、分號
        const isComma = ['，', '；'].includes(char);
        // 冒號（非標題內的）
        const isColon = ['：', ':'].includes(char);
        // 破折號結束
        const isDashEnd = char === '—' && nextChar !== '—';
        // 引號結束
        const isQuoteEndChar = quoteEnd.includes(char);
        // 下一個字元是句尾標點（需要合併）
        const nextIsSentenceEnd = nextChar && sentenceEndMarks.includes(nextChar);

        // 換行條件
        let shouldBreak = false;

        // 1. 引號結束 → 檢查下一個是否為句尾標點，如果是則先合併
        if (isQuoteEndChar) {
          if (nextIsSentenceEnd) {
            // 把下一個標點也加進來，不要讓它單獨一行
            currentLine += nextChar;
            i++; // 跳過下一個字元
          }
          shouldBreak = true;
        }
        // 2. 句尾標點 → 換行
        else if (isSentenceEnd) {
          shouldBreak = true;
        }
        // 3. 逗號且行長 >= 12 → 換行
        else if (isComma && currentLine.length >= 12) {
          shouldBreak = true;
        }
        // 4. 冒號且行長較短（可能是小標題）→ 換行
        else if (isColon && currentLine.length <= 20) {
          shouldBreak = true;
        }
        // 5. 破折號結束且行長 >= 10 → 換行
        else if (isDashEnd && currentLine.length >= 10) {
          shouldBreak = true;
        }

        if (shouldBreak) {
          result += currentLine.trim() + '\n';
          currentLine = '';
        }
      }

      // 剩餘內容
      if (currentLine.trim()) {
        result += currentLine.trim();
      }

      // 清理：移除只有單一標點的行
      const lines = result.split('\n');
      const cleanedLines = lines.filter(line => {
        const trimmed = line.trim();
        // 如果這行只有 1-2 個字元且都是標點，則移除
        if (trimmed.length <= 2 && /^[。！？，、；：.!?,;:\s]+$/.test(trimmed)) {
          return false;
        }
        return true;
      });

      return cleanedLines.join('\n').trim();
    };

    // 格式化每個段落，用空行連接
    const formatted = paragraphs
      .map(p => formatParagraph(p.trim()))
      .filter(p => p)
      .join('\n\n');

    setContent(formatted);
  };

  // AI Assistant: Rewrite content with ChatGPT
  const handleAIRewrite = async () => {
    if (!content.trim()) {
      toast.warning('請先輸入內容');
      return;
    }

    setIsAIGenerating(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('請先登入');
      }

      const toneDesc = aiTone === 'casual'
        ? '像朋友聊天，輕鬆自然，不做作'
        : '專業有深度，條理清晰，有說服力';

      // 根據長度選項決定字數限制
      let lengthRule;
      let maxTokens;
      if (aiLength === 'keep') {
        const currentLength = content.length;
        const minLength = Math.max(currentLength - 50, 100);
        const maxLength = currentLength + 50;
        lengthRule = `保持原文長度，約 ${minLength}-${maxLength} 字`;
        maxTokens = 800;
      } else if (aiLength === 'short') {
        lengthRule = '全文落在 200-300 字之間';
        maxTokens = 500;
      } else {
        lengthRule = '全文落在 300-500 字之間';
        maxTokens = 800;
      }

      const prompt = `你是一位專業的內容創作者，專注於寫出具有「故事感 + 顛覆感 + 互動性」的社群貼文。
請根據以下規則，將用戶的原始內容潤稿成一篇 Threads / IG 適合的短文：

[輸出規則]
1. 字數限制：${lengthRule}，保持緊湊。
2. 段落結構：全文切成 4–6 段，每段約 2–3 行。
3. 開頭規則：必須用一句吸睛開場，不超過 15 字。
4. 中段發展：用「鋪陳-反轉-思考」結構。
5. 結尾處理：不喊口號，留一句簡潔有力的 takeaway。
6. 語調：${toneDesc}
7. 保持原文的核心意思和觀點不變，但讓表達更有張力。
8. 避免使用 emoji、hashtag。
9. 【重要】直接輸出文章內容，不要加任何標題、標籤、說明文字（如「吸引人的開場：」「**」等），不要用 markdown 格式。

<原始內容>
${content}`;

      const response = await fetch('/api/ai/generate-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: prompt,
          maxTokens: maxTokens
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || errorData.message || errorData.error || '生成失敗';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      let newContent = data.content || data.text || '';

      // 格式化輸出
      newContent = newContent
        .replace(/。」/g, '」\n')
        .replace(/。(?!\n)/g, '。\n')
        .replace(/\n\n+/g, '\n\n')
        .trim();

      setContent(newContent);
      toast.success('潤稿完成！');
    } catch (error) {
      console.error('AI rewrite error:', error);
      toast.error(error.message || '潤稿失敗，請稍後再試');
    } finally {
      setIsAIGenerating(false);
    }
  };

  // Format date for display
  const formatScheduleDate = () => {
    const date = new Date(scheduleDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    let dateStr;
    if (targetDate.getTime() === today.getTime()) {
      dateStr = 'Today';
    } else if (targetDate.getTime() === tomorrow.getTime()) {
      dateStr = 'Tomorrow';
    } else {
      dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return `${dateStr} at ${scheduleTime}`;
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file =>
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );
    setMediaFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Character count
  const maxChars = 500;
  const charCount = content.length;
  const isOverLimit = charCount > maxChars;

  // Handle submit
  const handleSubmit = () => {
    if (!content.trim() && mediaFiles.length === 0) return;
    if (!selectedAccountId) {
      toast.warning('請先選擇帳號');
      return;
    }

    const postData = {
      content,
      mediaFiles,
      postType,
      scheduleDate,
      scheduleTime,
      accountId: selectedAccountId
    };

    onSubmit(postData);

    if (createAnother) {
      setContent('');
      setMediaFiles([]);
    }
  };

  // Handle save draft
  const handleSaveDraft = async () => {
    if (!content.trim() && mediaFiles.length === 0) {
      toast.warning('請輸入內容');
      return;
    }
    if (!selectedAccountId) {
      toast.warning('請先選擇帳號');
      return;
    }

    setIsPublishing(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('請先登入');
      }

      const response = await fetch('/api/posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          content_text: content,
          content_type: 'TEXT',
          status: 'draft'
        })
      });

      const result = await response.json();

      if (!result.success) {
        const errorMessage = result.error?.message || result.message || result.error || '儲存失敗';
        throw new Error(errorMessage);
      }

      toast.success('已儲存為草稿');
      onClose(true); // Pass true to reload posts

    } catch (error) {
      console.error('Save draft error:', error);
      toast.error(error.message || '儲存失敗，請稍後再試');
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle publish now (immediate)
  const handlePublishNow = async () => {
    if (!content.trim() && mediaFiles.length === 0) {
      toast.warning('請輸入內容');
      return;
    }
    if (!selectedAccountId) {
      toast.warning('請先選擇帳號');
      return;
    }

    setIsPublishing(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('請先登入');
      }

      // Call Threads posting API
      const response = await fetch('/api/threads/post-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountId: selectedAccountId,
          text: content
        })
      });

      const result = await response.json();

      if (!result.success) {
        const errorMessage = result.error?.message || result.message || result.error || '發布失敗';
        throw new Error(errorMessage);
      }

      toast.success('貼文已發布到 Threads！');
      onClose(true); // Pass true to reload posts

    } catch (error) {
      console.error('Publish error:', error);
      toast.error(error.message || '發布失敗，請稍後再試');
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle schedule post
  const handleSchedulePost = async () => {
    if (!content.trim() && mediaFiles.length === 0) {
      toast.warning('請輸入內容');
      return;
    }
    if (!selectedAccountId) {
      toast.warning('請先選擇帳號');
      return;
    }

    setIsPublishing(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('請先登入');
      }

      // Create scheduled post in database
      const scheduledAt = new Date(scheduleDate);
      const [hours, minutes] = scheduleTime.split(':').map(Number);
      scheduledAt.setHours(hours, minutes, 0, 0);

      const response = await fetch('/api/posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          content_text: content,
          content_type: 'TEXT',
          status: 'scheduled',
          scheduled_at: scheduledAt.toISOString()
        })
      });

      const result = await response.json();

      if (!result.success) {
        const errorMessage = result.error?.message || result.message || result.error || '排程失敗';
        throw new Error(errorMessage);
      }

      toast.success(`貼文已排程於 ${formatScheduleDate()}`);
      onClose(true); // Pass true to reload posts

    } catch (error) {
      console.error('Schedule error:', error);
      toast.error(error.message || '排程失敗，請稍後再試');
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Create Post
            </h2>
          </div>
          <button
            onClick={onClose}
            className="bg-transparent border-none p-2 cursor-pointer rounded-md text-gray-500"
          >
            <Icon icon="mdi:close" width={24} />
          </button>
        </div>

        {/* Account & Post Type Selector */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          {/* Account Dropdown */}
          <AccountDropdown
            value={selectedAccountId}
            onChange={handleAccountChange}
            placeholder="選擇帳號"
            size="md"
            fullWidth={false}
            className="min-w-[200px]"
          />

          {/* Post Type (for Instagram) */}
          {selectedAccount?.platform === 'instagram' && (
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {['Post', 'Reel', 'Story'].map(type => (
                <button
                  key={type}
                  onClick={() => setPostType(type.toLowerCase())}
                  className={`px-3 py-1.5 border-none rounded-md text-[13px] font-medium cursor-pointer ${
                    postType === type.toLowerCase()
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'bg-transparent text-gray-500'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Text Area */}
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What would you like to share?"
            className="w-full min-h-[120px] border-none outline-none resize-none text-base leading-relaxed text-gray-900 font-inherit"
          />

          {/* Media Upload Area */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`mt-4 p-6 border-2 border-dashed rounded-xl text-center cursor-pointer transition-all ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={(e) => handleFiles(Array.from(e.target.files))}
              className="hidden"
            />
            <Icon icon="mdi:cloud-upload-outline" width={32} className="text-gray-400 mb-2" />
            <div className="text-sm text-gray-500">
              Drag & drop files here, or <span className="text-blue-500 font-medium">browse</span>
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Supports images and videos
            </div>
          </div>

          {/* Media Preview */}
          {mediaFiles.length > 0 && (
            <div className="flex gap-2 mt-4 flex-wrap">
              {mediaFiles.map((file, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt=""
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 border-none text-white cursor-pointer flex items-center justify-center"
                  >
                    <Icon icon="mdi:close" width={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="border-t border-gray-100">
          {/* First Row: Basic Tools */}
          <div className="flex items-center gap-1 px-5 py-3">
            <button
              onClick={handleAIFormat}
              className="bg-transparent border-none p-2 cursor-pointer rounded-md text-gray-500 flex items-center gap-1 text-[13px] hover:bg-gray-100 transition-colors"
              title="自動排版：標點換行、保持引用完整"
            >
              <Icon icon="mdi:text-box-edit-outline" width={20} />
              智慧排版
            </button>

            <button
              onClick={() => setShowAIOptions(!showAIOptions)}
              disabled={isAIGenerating}
              className={`bg-transparent border-none p-2 cursor-pointer rounded-md flex items-center gap-1 text-[13px] transition-colors disabled:opacity-50 ${
                showAIOptions ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-100'
              }`}
              title="AI 潤稿"
            >
              <Icon icon={isAIGenerating ? "mdi:loading" : "mdi:robot"} width={20} className={isAIGenerating ? "animate-spin" : ""} />
              {isAIGenerating ? '生成中...' : 'AI 潤稿'}
            </button>

            <div className="flex-1" />

            {/* Character Count */}
            <div className={`text-[13px] ${isOverLimit ? 'text-red-500' : 'text-gray-500'}`}>
              {charCount}/{maxChars}
            </div>
          </div>

          {/* Second Row: AI Options (Collapsible) */}
          {showAIOptions && (
            <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-t border-gray-100">
              {/* Length Options */}
              <div className="flex items-center gap-2">
                {[
                  { id: 'keep', label: '保持原長' },
                  { id: 'short', label: '短文' },
                  { id: 'long', label: '長文' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setAiLength(opt.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      aiLength === opt.id
                        ? 'bg-gray-200 text-gray-900 border border-gray-300'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="h-5 w-px bg-gray-300" />

              {/* Tone Options */}
              <div className="flex items-center gap-2">
                {[
                  { id: 'casual', label: '輕鬆自然', icon: 'mdi:emoticon-happy-outline' },
                  { id: 'professional', label: '專業深度', icon: 'mdi:briefcase-outline' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setAiTone(opt.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      aiTone === opt.id
                        ? 'bg-gray-200 text-gray-900 border border-gray-300'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <Icon icon={opt.icon} width={14} />
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="flex-1" />

              {/* AI Rewrite Button */}
              <button
                onClick={handleAIRewrite}
                disabled={isAIGenerating}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white border-none cursor-pointer hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon icon={isAIGenerating ? "mdi:loading" : "mdi:sparkles"} width={16} className={isAIGenerating ? "animate-spin" : ""} />
                {isAIGenerating ? '生成中...' : '開始潤稿'}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-gray-50">
          {/* Left: Save Draft */}
          <button
            onClick={handleSaveDraft}
            disabled={isPublishing}
            className="px-4 py-2.5 bg-transparent border border-gray-200 rounded-lg text-gray-700 text-sm font-medium cursor-pointer hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            存為草稿
          </button>

          {/* Right: Publish Actions */}
          <div className="flex items-center gap-2">
            {/* Schedule Picker */}
            <div className="relative">
              <button
                onClick={() => setShowSchedulePicker(!showSchedulePicker)}
                className="flex items-center gap-2 px-3 py-2.5 bg-gray-100 rounded-lg text-[13px] text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer border-none"
              >
                <Icon icon="mdi:calendar-clock" width={16} />
                {formatScheduleDate()}
                <Icon icon="mdi:chevron-down" width={14} />
              </button>

              {/* Schedule Picker Popup */}
              {showSchedulePicker && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowSchedulePicker(false)}
                  />
                  <div className="absolute bottom-full right-0 mb-2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-[280px] z-50">
                    <div className="text-sm font-medium text-gray-900 mb-3">選擇排程時間</div>

                    {/* Date Input */}
                    <div className="mb-3">
                      <label className="text-xs text-gray-500 mb-1 block">日期</label>
                      <input
                        type="date"
                        value={scheduleDate instanceof Date ? scheduleDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                        onChange={(e) => setScheduleDate(new Date(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>

                    {/* Time Input */}
                    <div className="mb-3">
                      <label className="text-xs text-gray-500 mb-1 block">時間</label>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>

                    {/* Quick Times */}
                    <div className="flex flex-wrap gap-2">
                      {['09:00', '12:30', '18:00', '20:30'].map(time => (
                        <button
                          key={time}
                          onClick={() => {
                            setScheduleTime(time);
                            setShowSchedulePicker(false);
                          }}
                          className={`px-3 py-1.5 rounded-md text-xs border-none cursor-pointer ${
                            scheduleTime === time
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Schedule Button */}
            <button
              onClick={handleSchedulePost}
              disabled={(!content.trim() && mediaFiles.length === 0) || isPublishing}
              className={`px-4 py-2.5 border-none rounded-lg font-medium text-sm flex items-center gap-1.5 transition-colors ${
                (!content.trim() && mediaFiles.length === 0) || isPublishing
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 cursor-pointer hover:bg-gray-300'
              }`}
            >
              <Icon icon="mdi:clock-outline" width={16} />
              排程發布
            </button>

            {/* Publish Now Button */}
            <button
              onClick={handlePublishNow}
              disabled={(!content.trim() && mediaFiles.length === 0) || isPublishing}
              className={`px-5 py-2.5 text-white border-none rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-colors ${
                (!content.trim() && mediaFiles.length === 0) || isPublishing
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-[#007AFF] cursor-pointer hover:bg-[#0066D6]'
              }`}
            >
              {isPublishing ? (
                <>
                  <Icon icon="mdi:loading" width={16} className="animate-spin" />
                  發布中...
                </>
              ) : (
                <>
                  <Icon icon="mdi:send" width={16} />
                  立即發布
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PostsPage;
